import { type ChatRole, assertNever } from '@matter_extractor/shared';

/**
 * The timing rules of a live conversation (chat spec §7, §8, §11).
 *
 * PURE, the régime `overviewRefetchInterval` and `parcoursStopAt` already hold
 * in this package: what decides how long a screen waits, and what it calls the
 * other person, has to be replayable without a socket and without a clock.
 * Nothing here connects, retries or renders - it answers « how long » and « what
 * does that mean » from numbers a test hands it.
 */

// ── Reconnecting ────────────────────────────────────────────────────────────

/** The first wait, and the shortest. */
export const CHAT_RECONNECT_BASE_MS = 500;

/**
 * The longest wait between two attempts.
 *
 * Ten seconds and not the thirty a backoff usually caps at, because of what
 * actually cuts these sockets: §8 opens on it - every `git push` on `main`
 * recreates the backend container, so the normal outage is a deploy and lasts
 * seconds. A thirty-second ceiling would leave two people looking at a
 * « Reconnexion » banner long after the server came back.
 *
 * There is no jitter, and that is a decision rather than an omission: jitter
 * exists to spread a herd, and the herd here is TWO browsers. What it would cost
 * is a delay nobody can predict from the code, on the one screen where somebody
 * is waiting.
 */
export const CHAT_RECONNECT_MAX_MS = 10_000;

/**
 * How long to wait before attempt number `attempt` (0-based).
 *
 * Exponential and capped: 500 ms, 1 s, 2 s, 4 s, 8 s, then 10 s for ever. The
 * doubling is what keeps a backend that is down for an hour from being asked
 * seven thousand times, and the floor of 500 ms is what makes a deploy feel
 * like a hiccup instead of an outage.
 *
 * It never gives up. A conversation is a one-way door (§4.5) and the person on
 * the other side may be composing a hard answer: a client that stopped trying
 * after N attempts would need the person to notice a dead screen and refresh,
 * which is exactly what the queue and the persisted draft exist to make
 * unnecessary.
 */
export function chatReconnectDelayMs(attempt: number): number {
  const bounded = Math.max(0, Math.floor(attempt));
  // `2 ** bounded` overflows into `Infinity` long before it matters, and
  // `Math.min` handles that correctly - but the exponent is clamped anyway so
  // the arithmetic stays finite and readable in a debugger.
  const doubling = CHAT_RECONNECT_BASE_MS * 2 ** Math.min(bounded, 20);
  return Math.min(doubling, CHAT_RECONNECT_MAX_MS);
}

// ── The two slow reads under the socket ─────────────────────────────────────

/**
 * The cadence of the two REST reads the conversation keeps alive beside its
 * socket - one number, because they share one argument.
 *
 * The socket carries the conversation (§7). What it CANNOT carry is:
 *
 *   - **the closure decided by the other side.** Only the interviewer can end
 *     an interview (§11), and the guest's screen has to leave the conversation
 *     for her end screen when he does. Nothing in this lot's socket says so, so
 *     the room's own state is re-read.
 *   - **the presence stamp** the other side needs to tell « gone » from
 *     « thinking » (§11). It is written by this very read - see
 *     `chat/presence.ts` in the backend - and read off the peer.
 *
 * Twenty seconds is slow enough that two screens left open cost almost nothing
 * (the ceiling of §5 counts failures only, so a holder of a real link spends
 * none of it), and quick enough that neither of the two facts above is stale
 * enough to mislead: a closure lands within one beat, and the presence
 * indicator's own threshold is six beats wide.
 */
export const CHAT_LIVE_BEAT_MS = 20_000;

// ── Reading the other person ────────────────────────────────────────────────

/**
 * How long a peer has to be unseen before the screen says so - §11's « deux
 * minutes ».
 *
 * It is deliberately much longer than the beat above: the failure to avoid is
 * calling a thinking guest « déconnecté », and the whole reason the guest has
 * no exit control is that this indicator is what tells the interviewer
 * otherwise. Four missed beats before a word appears on screen.
 */
export const CHAT_PEER_AWAY_AFTER_MS = 120_000;

/** What the screen can say about the other side. */
export type ChatPeerPresence =
  | { state: 'present' }
  /** Unseen for a while, and for how many whole minutes. */
  | { state: 'away'; minutes: number }
  /** No stamp at all: she has not been seen since the room was struck. */
  | { state: 'unknown' };

/**
 * Reads the peer's presence off ONE stamp, and never off her messages.
 *
 * Deriving it from the last message is the tempting shortcut and it is wrong:
 * a two-minute silence in an interview is somebody thinking, which is the most
 * valuable thing in the transcript, and §11 asks this indicator to tell that
 * apart from a departure. So the only input is `lastSeenAt`, written by a beat
 * that means « my tab is here » and nothing else.
 */
export function chatPeerPresence(input: {
  lastSeenAt: string | null;
  now: number;
  awayAfterMs?: number;
}): ChatPeerPresence {
  if (input.lastSeenAt === null) return { state: 'unknown' };
  const seen = Date.parse(input.lastSeenAt);
  // An unreadable stamp is not a departure. Same soft-fail direction as
  // `chat/expiry.ts`: at worst the screen is a little too optimistic, whereas
  // « déconnecté » on a live guest invites the interviewer to close on somebody
  // who is still there.
  if (Number.isNaN(seen)) return { state: 'unknown' };
  const silence = input.now - seen;
  if (silence < (input.awayAfterMs ?? CHAT_PEER_AWAY_AFTER_MS)) return { state: 'present' };
  return { state: 'away', minutes: Math.max(1, Math.floor(silence / 60_000)) };
}

/**
 * What the other side is CALLED, and it is a role and never a person (§4.3).
 *
 * The interviewer's screen says « Invité » because that is the role he is
 * waiting on; the guest's says « Interlocuteur », which names a role too - and
 * carries not one thing about who is behind it, which is the whole protocol.
 */
export function chatPeerNoun(peerRole: ChatRole): string {
  return peerRole === 'guest' ? 'Invité' : 'Interlocuteur';
}

/** The pill of S3 and S4, worded from the role and the stamp. */
export function chatPeerPresenceLabel(presence: ChatPeerPresence, peerRole: ChatRole): string {
  const noun = chatPeerNoun(peerRole);
  switch (presence.state) {
    case 'present':
      return `${noun} connecté`;
    case 'away':
      return `${noun} déconnecté, ${presence.minutes} min`;
    case 'unknown':
      // No stamp: « déconnecté » with no duration. Saying « 0 min » would claim
      // a measurement nobody made, and saying « connecté » would be a lie on
      // the one screen that must not tell it.
      return `${noun} déconnecté`;
    default:
      return assertNever(presence, 'chat peer presence');
  }
}
